// CesarFilho 932048016
// src/pages/workforce/OperatorSkillsPage.tsx
// -----------------------------------------------------------------------------
// Tela administrativa para vincular/desvincular SKILLS a um OPERADOR.
//
// Decisões de design:
// - Continua usando o "service" central (listSkills/getOperatorSkills/setOperatorSkills).
// - Para evitar 404 em ambientes onde o endpoint do operador ainda não exista,
//   há FALLBACKS diretos para os endpoints de skills "por usuário".
// - Link "Voltar" SEMPRE retorna para /workforce/operators.
// - Comentários explicam o racional (para manutenção futura).
// -----------------------------------------------------------------------------

import React, { useEffect, useMemo, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import api from '@/lib/api'

// --- Contratos do service oficial (mantidos) --------------------------------
import {
  getOperatorSkills,
  listSkills,
  listWarehousesByCompany,
  setOperatorSkills,
  Skill,
  WarehouseLite,
} from '../../api/workforce'

// Linha típica retornada em getOperatorSkills().skills
// (Ex.: tabela through "OperatorSkill" → { skill: Skill })
type OperatorSkillRow = { skill: Skill }

// Resposta mínima esperada de getOperatorSkills()
type OperatorSkillsResponse = {
  id: number
  user_name?: string
  skills: OperatorSkillRow[]
  // ADR-0057: company (id) + base (FK id) + armazéns autorizados (id/code/name).
  company?: number | null
  warehouse?: number | null
  warehouses?: WarehouseLite[]
}

// --------------------------------- Helpers ----------------------------------

/** Extrai array de resposta que pode vir como array ou {results: [...]} */
function asArray<T>(data: any): T[] {
  if (!data) return []
  if (Array.isArray(data)) return data as T[]
  if (Array.isArray(data.results)) return data.results as T[]
  if (Array.isArray(data.items)) return data.items as T[]
  return []
}

/** Tenta obter o nome do usuário via endpoints padrão do projeto. */
async function tryFetchUserName(userId: number): Promise<string | null> {
  const tries = [`/users/users/${userId}/`, `/auth/users/${userId}/`]
  for (const path of tries) {
    try {
      const { data } = await api.get<any>(path)
      const first = (data?.first_name ?? '').trim()
      const last = (data?.last_name ?? '').trim()
      const full = `${first} ${last}`.trim()
      return full || data?.username || null
    } catch {
      /* próxima rota */
    }
  }
  return null
}

/**
 * Fallback para buscar skills já atribuídas ao usuário quando o endpoint
 * de operador não existir: tenta "by-user/:id" e depois querystring "?user=:id".
 * Normaliza o retorno para OperatorSkillsResponse.
 */
async function fallbackGetOperatorSkills(
  userId: number,
): Promise<OperatorSkillsResponse> {
  // Tenta by-user/:id/
  try {
    const { data } = await api.get<any>(`/workforce/skills/by-user/${userId}/`)
    const arr = asArray<any>(data)
    const rows: OperatorSkillRow[] = arr.map((x: any) => {
      // Alguns backends retornam "skill" dentro; outros já retornam o próprio Skill
      const skill: Skill =
        (x?.skill as Skill) ??
        ({
          id: x?.id,
          code: x?.code ?? x?.skill_code,
          name: x?.name ?? x?.skill_name,
          is_active: x?.is_active ?? x?.active ?? true,
        } as Skill)
      return { skill }
    })
    const name = (await tryFetchUserName(userId)) || `#${userId}`
    return { id: userId, user_name: name, skills: rows }
  } catch {
    /* tenta a próxima forma */
  }

  // Tenta ?user=:id
  try {
    const { data } = await api.get<any>(
      `/workforce/skills/?user=${userId}&page_size=200`,
    )
    const arr = asArray<any>(data)
    const rows: OperatorSkillRow[] = arr.map((x: any) => {
      const skill: Skill =
        (x?.skill as Skill) ??
        ({
          id: x?.id,
          code: x?.code ?? x?.skill_code,
          name: x?.name ?? x?.skill_name,
          is_active: x?.is_active ?? x?.active ?? true,
        } as Skill)
      return { skill }
    })
    const name = (await tryFetchUserName(userId)) || `#${userId}`
    return { id: userId, user_name: name, skills: rows }
  } catch {
    /* sem dados mesmo; nome cai no fallback abaixo */
  }

  return { id: userId, user_name: `#${userId}`, skills: [] }
}

/**
 * Envolve o service oficial com fallback seguro:
 * - Primeiro tenta getOperatorSkills(opId) (service).
 * - Se falhar (ex.: 404), usa fallbackGetOperatorSkills().
 */
async function safeGetOperatorSkills(
  opId: number,
): Promise<OperatorSkillsResponse> {
  try {
    const raw = (await getOperatorSkills(opId)) as any
    // Se o service retornar exatamente o formato esperado, ok.
    if (
      raw &&
      typeof raw === 'object' &&
      ('skills' in raw || 'user_name' in raw)
    ) {
      return raw as OperatorSkillsResponse
    }
    // Alguns services retornam {id, user, skills:[Skill]} — normaliza:
    const userName =
      raw?.user_name ||
      (raw?.user
        ? [raw.user.first_name, raw.user.last_name].filter(Boolean).join(' ')
        : null) ||
      (await tryFetchUserName(opId)) ||
      `#${raw?.id ?? opId}`

    const skillsRows: OperatorSkillRow[] = asArray<any>(raw?.skills).map(
      (x: any) => {
        const skill: Skill =
          (x?.skill as Skill) ??
          ({
            id: x?.id,
            code: x?.code ?? x?.skill_code,
            name: x?.name ?? x?.skill_name,
            is_active: x?.is_active ?? x?.active ?? true,
          } as Skill)
        return { skill }
      },
    )

    return {
      id: Number(raw?.id ?? opId),
      user_name: userName,
      skills: skillsRows,
    }
  } catch (err: any) {
    // Fallback “por usuário” (evita 404 do endpoint de operador)
    return fallbackGetOperatorSkills(opId)
  }
}

// --------------------------------- Página -----------------------------------

export default function OperatorSkillsPage() {
  // Suporta /workforce/operators/:id/skills e /workforce/users/:id/skills
  const params = useParams<{ id: string }>()
  const opId = Number(params.id)

  // Nome do operador (header)
  const [opName, setOpName] = useState<string>('-')
  // Catálogo completo de skills (para montar a lista de marcação)
  const [allSkills, setAllSkills] = useState<Skill[]>([])
  // Conjunto de IDs selecionados (skills vinculadas ao operador)
  const [selected, setSelected] = useState<number[]>([])
  // ADR-0057 F4b — secção Armazéns.
  const [warehouses, setWarehouses] = useState<WarehouseLite[]>([]) // todos os da company
  const [authorized, setAuthorized] = useState<number[]>([]) // ids autorizados (checkbox)
  const [baseId, setBaseId] = useState<number | null>(null) // armazém base (radio)
  const [loadedBaseId, setLoadedBaseId] = useState<number | null>(null) // base carregada (aviso)
  // Flags de estado
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string>('')

  // Há alterações pendentes? (na versão simples: ter algo selecionado já habilita salvar)
  const hasChanges = useMemo(() => selected.length >= 0, [selected])

  // Agrupa por category: Processos (PICK/PUTAWAY/...) + Equipamentos (101-120). Sem category
  // (edge) cai em Equipamentos. Assim o admin vê e marca TODAS (não só a 1a página numérica).
  const groups = useMemo(
    () => [
      {
        key: 'PROCESS',
        label: 'Processos',
        items: allSkills.filter((s) => s.category === 'PROCESS'),
      },
      {
        key: 'EQUIPMENT',
        label: 'Equipamentos',
        items: allSkills.filter((s) => s.category !== 'PROCESS'),
      },
    ],
    [allSkills],
  )

  // Carrega dados iniciais
  async function load() {
    if (!Number.isFinite(opId) || opId <= 0) {
      setError('Operador inválido.')
      return
    }
    setLoading(true)
    setError('')
    try {
      // Busca dados do operador (com skills) + catálogo de skills em paralelo
      const [opRaw, skillsRaw] = await Promise.all([
        safeGetOperatorSkills(opId),
        listSkills(''), // service oficial; já normaliza {results}
      ])

      // Nome amigável
      setOpName(opRaw.user_name || `#${opRaw.id}`)

      // Catálogo (pode vir como array simples)
      setAllSkills(asArray<Skill>(skillsRaw))

      // Normaliza as skills atualmente vinculadas → array de IDs
      const current = asArray<OperatorSkillRow>(opRaw.skills).map(
        (row) => row.skill.id,
      )
      setSelected(current)

      // ADR-0057 F4b — armazéns: autorizados + base (do operador) e lista da company.
      const authorizedWh = Array.isArray(opRaw.warehouses)
        ? opRaw.warehouses
        : []
      const baseWh = opRaw.warehouse ?? null
      const companyId = opRaw.company ?? null

      // Lista completa da company (endpoint reutilizado); fallback: só os autorizados.
      let whList: WarehouseLite[] = authorizedWh
      if (companyId) {
        try {
          whList = await listWarehousesByCompany(companyId)
        } catch {
          whList = authorizedWh
        }
      }
      // União (garante que autorizados/base aparecem mesmo que a lista os omita).
      const byId = new Map<number, WarehouseLite>()
      for (const w of whList)
        byId.set(w.id, { id: w.id, code: w.code, name: w.name })
      for (const w of authorizedWh) if (!byId.has(w.id)) byId.set(w.id, w)
      setWarehouses(
        [...byId.values()].sort((a, b) => a.code.localeCompare(b.code)),
      )
      setAuthorized(authorizedWh.map((w) => w.id))
      setBaseId(baseWh)
      setLoadedBaseId(baseWh)
    } catch (e: any) {
      setError(e?.message || 'Falha ao carregar dados')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opId])

  // Marca/desmarca uma skill
  function toggle(skillId: number) {
    setSelected((prev) =>
      prev.includes(skillId)
        ? prev.filter((x) => x !== skillId)
        : [...prev, skillId],
    )
  }

  // ADR-0057 F4b — autoriza/desautoriza um armazém. A BASE não se desmarca (o backend recusa;
  // a UI evita o erro). Marcar a checkbox "liga" o radio da linha (fica selecionável).
  function toggleAuthorized(id: number) {
    if (id === baseId) return // base não se desmarca
    setAuthorized((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    )
  }

  // Escolhe a base — só permitido em armazém autorizado (o radio está DESATIVADO se não for).
  function chooseBase(id: number) {
    if (!authorized.includes(id)) return
    setBaseId(id)
  }

  // Persistência:
  // - Primeiro tenta o endpoint do service: setOperatorSkills(opId, [ids]).
  // - Se falhar, mostra erro claro (o fallback "manual" depende do backend).
  async function save() {
    if (!Number.isFinite(opId) || opId <= 0) return

    // ADR-0057 F4b — aviso: mudar a base altera o default dos turnos futuros.
    if (baseId !== loadedBaseId) {
      const ok = window.confirm(
        'Mudar o armazém base altera o default dos turnos futuros deste operador. Continuar?',
      )
      if (!ok) return
    }

    setLoading(true)
    setError('')
    try {
      // Uma só chamada: skills + armazéns autorizados + base.
      await setOperatorSkills(opId, selected, {
        warehouse_ids: authorized,
        base_warehouse_id: baseId ?? undefined,
      })
      await load() // garante refletir o estado atual do servidor
      alert('Guardado com sucesso!')
    } catch (e: any) {
      // Mostra a MENSAGEM do backend; 403 -> exige supervisor.
      const status = e?.response?.status
      const detail = e?.response?.data?.detail
      const msg =
        status === 403
          ? 'Sem permissão: gravar exige perfil de supervisor.'
          : detail || e?.message || 'Erro ao guardar.'
      setError(msg)
      alert(msg)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="p-6 max-w-3xl mx-auto">
      {/* Cabeçalho + link de retorno */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold mb-2">
          Operador • Skills e Armazéns
        </h1>
        {/* IMPORTANTE: volta SEMPRE para a lista de OPERADORES */}
        <Link
          to="/workforce/operators"
          className="text-sm underline opacity-80 hover:opacity-100"
        >
          Voltar para lista de operadores
        </Link>
      </div>

      <p className="opacity-70 mb-6">{opName}</p>

      {/* Mensagens de carregamento/erro, seguindo seu padrão visual */}
      {loading && <div className="mb-3 opacity-70">Carregando…</div>}

      {!!error && (
        <div className="mb-3 px-3 py-2 rounded bg-red-900/30 text-red-200 border border-red-700">
          {error}
        </div>
      )}

      {/* ADR-0057 F4b — ARMAZÉNS (PRIMEIRA secção). Mesma anatomia dos cartões de skills;
          diferença única: coluna "Base" à direita (radio por linha). */}
      <div className="space-y-4">
        <div className="border border-neutral-800 rounded-lg p-4">
          <h2 className="text-xs font-semibold uppercase opacity-60 mb-2">
            Armazéns
          </h2>
          {/* Cabeçalho de colunas discreto: (vazio) / Código / Nome / Base */}
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-wide opacity-40 mb-1">
            <span className="w-4 shrink-0" />
            <span className="min-w-[90px]">Código</span>
            <span className="flex-1">Nome</span>
            <span className="shrink-0">Base</span>
          </div>
          <div className="space-y-2">
            {warehouses.map((w) => (
              <div key={w.id} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  className="w-4 shrink-0"
                  checked={authorized.includes(w.id)}
                  disabled={w.id === baseId} // a base não se desmarca
                  onChange={() => toggleAuthorized(w.id)}
                />
                <span className="min-w-[90px] font-mono">{w.code}</span>
                <span className="flex-1 opacity-80">{w.name}</span>
                <input
                  type="radio"
                  name="base-warehouse"
                  className="shrink-0"
                  checked={baseId === w.id}
                  disabled={!authorized.includes(w.id)} // radio só em autorizados
                  onChange={() => chooseBase(w.id)}
                />
              </div>
            ))}
            {warehouses.length === 0 && !loading && (
              <div className="opacity-60 text-sm">Nenhum armazém.</div>
            )}
          </div>
          <p className="text-[11px] opacity-50 mt-2">
            A base não se desmarca. O radio só liga em armazéns autorizados.
          </p>
        </div>

        {groups.map((g) => (
          <div key={g.key} className="border border-neutral-800 rounded-lg p-4">
            <h2 className="text-xs font-semibold uppercase opacity-60 mb-2">
              {g.label}
            </h2>
            <div className="space-y-2">
              {g.items.map((s: Skill) => (
                <label key={s.id} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={selected.includes(s.id)}
                    onChange={() => toggle(s.id)}
                  />
                  <span className="min-w-[90px] font-mono">{s.code}</span>
                  <span className="opacity-80">{s.name}</span>
                  {!s.is_active && (
                    <span className="text-xs px-2 py-0.5 ml-2 rounded bg-yellow-700">
                      inativa
                    </span>
                  )}
                </label>
              ))}
              {g.items.length === 0 && (
                <div className="opacity-60 text-sm">Nenhuma.</div>
              )}
            </div>
          </div>
        ))}
        {allSkills.length === 0 && !loading && (
          <div className="opacity-60">Nenhuma skill cadastrada.</div>
        )}
      </div>

      {/* Ações */}
      <div className="mt-4 flex items-center justify-end gap-3">
        <button
          onClick={save}
          disabled={loading || !hasChanges}
          className="px-4 py-2 rounded bg-blue-600 disabled:opacity-60"
        >
          Salvar
        </button>
      </div>
    </div>
  )
}
